import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSEO } from '@/hooks/useSEO';
import { BACKEND_URL } from '@/components/verify/verifyShared';
import VerifyMethodPicker from '@/components/verify/VerifyMethodPicker';
import VerifyDnsStep from '@/components/verify/VerifyDnsStep';
import VerifyHtmlStep from '@/components/verify/VerifyHtmlStep';
import VerifyMetaStep from '@/components/verify/VerifyMetaStep';
import VerifySuccess from '@/components/verify/VerifySuccess';

const STEP_FOR_METHOD = {
  dns: VerifyDnsStep, dns_txt: VerifyDnsStep,
  html_file: VerifyHtmlStep, meta_tag: VerifyMetaStep,
};

/* Full-page focused wrapper (module-level so the subtree isn't remounted on
   every poll, which would reset Collapsible/Copy state). */
function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '2rem 1.25rem' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="font-body"
          style={{ background: 'transparent', border: 'none', color: 'var(--ts)',
                   fontSize: '0.82rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}
        >
          ← Back to dashboard
        </button>
        {children}
      </div>
    </div>
  );
}

export default function VerifyDomainPage() {
  useSEO({
    title: 'Verify Domain | Acodia',
    description: 'Verify ownership of your domain before Acodia Watch monitoring begins.',
    path: '/dashboard/domains/verify',
    robots: 'noindex,nofollow',
  });
  const { domainId } = useParams();
  const { authFetch } = useAuth();

  const [screen, setScreen] = useState('loading'); // loading|picker|step|success|error
  const [domain, setDomain] = useState('');
  const [method, setMethod] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState('unverified');
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [subGranted, setSubGranted] = useState(0);
  const [autoStopped, setAutoStopped] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const startRef = useRef(Date.now());
  const pollRef = useRef(null);
  const runCheckRef = useRef(null);

  // ── Start / resume a method ──
  const beginMethod = useCallback(async (chosenMethod, resume = false) => {
    setStarting(true);
    setErrorMsg('');
    try {
      const body = resume ? { domain_id: domainId } : { domain_id: domainId, method: chosenMethod };
      const res = await authFetch(`${BACKEND_URL}/api/verify/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorMsg((data.detail && data.detail.error) || data.detail || 'Could not start verification.');
        return;
      }
      setInstructions({ ...(data.instructions || {}), token: data.token });
      setToken(data.token);
      setMethod(chosenMethod);
      setLastCheck(null);
      setAutoStopped(false);
      startRef.current = Date.now();
      setScreen('step');
    } finally {
      setStarting(false);
    }
  }, [domainId, authFetch]);

  // ── Initial status fetch → choose screen ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/verify/status/${encodeURIComponent(domainId)}`);
        if (!res.ok) { if (!cancelled) { setErrorMsg('Domain not found.'); setScreen('error'); } return; }
        const data = await res.json();
        if (cancelled) return;
        setDomain(data.domain || '');
        const st = data.verification_status || (data.verified ? 'verified' : 'unverified');
        setStatus(st);
        setMethod(data.verification_method || null);
        setVerifiedAt(data.verified_at || null);
        setLastCheck(data.last_check_result || null);

        if (st === 'verified' || st === 'expired') {
          setScreen('success');
        } else if ((st === 'pending' || st === 'degraded') &&
                   data.verification_method && data.verification_method !== 'parent_domain') {
          await beginMethod(data.verification_method, true); // resume (keeps token)
        } else {
          setScreen('picker');
        }
      } catch {
        if (!cancelled) { setErrorMsg('Could not load verification status.'); setScreen('error'); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  // ── Run a real verification check (POST /verify/check) ──
  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/verify/check`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain_id: domainId }),
      });
      if (res.status === 429) return; // rate-limited — skip this tick
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setLastCheck(data.last_check_result || {
        status: data.status, found_value: data.found_value,
        expected_value: data.expected_value, error_message: data.error_message,
      });
      if (data.verification_status) setStatus(data.verification_status);
      if (data.subdomains_auto_granted) setSubGranted(data.subdomains_auto_granted);
      if (data.verification_status === 'verified') {
        setVerifiedAt(data.verified_at || new Date().toISOString());
        if (pollRef.current) clearTimeout(pollRef.current);
        setTimeout(() => setScreen('success'), 1500);
      }
    } catch { /* ignore */ } finally {
      setChecking(false);
    }
  }, [domainId, authFetch]);

  useEffect(() => { runCheckRef.current = runCheck; }, [runCheck]);

  // ── Auto-poll with backoff while on a step screen ──
  useEffect(() => {
    if (screen !== 'step') return undefined;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      const elapsed = Date.now() - startRef.current;
      let interval;
      if (elapsed < 60 * 1000) interval = 10 * 1000;
      else if (elapsed < 5 * 60 * 1000) interval = 30 * 1000;
      else if (elapsed < 30 * 60 * 1000) interval = 60 * 1000;
      else { setAutoStopped(true); return; }
      if (runCheckRef.current) runCheckRef.current();
      pollRef.current = setTimeout(tick, interval);
    };
    pollRef.current = setTimeout(tick, 10 * 1000);
    return () => { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [screen]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/domains/${encodeURIComponent(domainId)}/verify/download`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `acodia-verify-${token}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }, [domainId, authFetch, token]);

  const switchMethod = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    setLastCheck(null);
    setScreen('picker');
  }, []);

  const pillState = (() => {
    if (checking) return 'checking';
    if (status === 'verified') return 'verified';
    if (!lastCheck) return 'waiting';
    if (lastCheck.status === 'mismatch') return 'mismatch';
    if (lastCheck.status === 'error') return 'error';
    if (lastCheck.status === 'not_found') return 'not_found';
    return 'waiting';
  })();

  const autoNote = autoStopped
    ? "We've stopped auto-checking. Click 'Check now' when you've made the change."
    : 'Auto-checking every 10 seconds…';

  // ── Render ──
  if (screen === 'loading') {
    return <Shell><p className="font-body" style={{ color: 'var(--ts)' }}>Loading…</p></Shell>;
  }
  if (screen === 'error') {
    return (
      <Shell>
        <p className="font-body" style={{ color: 'var(--danger)' }}>{errorMsg || 'Something went wrong.'}</p>
      </Shell>
    );
  }
  if (screen === 'success') {
    return (
      <Shell>
        <VerifySuccess
          domain={domain}
          verifiedAt={verifiedAt}
          subdomainsGranted={subGranted}
          status={status}
          onReverify={() => setScreen('picker')}
        />
      </Shell>
    );
  }
  if (screen === 'picker') {
    return (
      <Shell>
        {errorMsg && <p className="font-body" style={{ color: 'var(--danger)', marginBottom: '1rem' }}>{errorMsg}</p>}
        <VerifyMethodPicker domain={domain} onContinue={beginMethod} starting={starting} />
      </Shell>
    );
  }

  const StepComponent = STEP_FOR_METHOD[method] || VerifyDnsStep;
  return (
    <Shell>
      {status === 'degraded' && (
        <div className="font-body" style={{ marginBottom: '1rem', padding: '0.7rem 0.9rem', borderRadius: 8,
              background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.3)',
              color: 'var(--warn)', fontSize: '0.85rem' }}>
          We&apos;re trying to re-verify your domain — please confirm the record below is still in place.
        </div>
      )}
      <StepComponent
        domain={domain}
        instructions={instructions}
        pillState={pillState}
        lastCheck={lastCheck}
        checking={checking}
        onCheckNow={runCheck}
        onSwitchMethod={switchMethod}
        autoNote={autoNote}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </Shell>
  );
}
